/**
 * Registry per mappare endpoint form a pagine full-screen (invece che modali)
 *
 * Alcune entità (come i corsi) richiedono una pagina dedicata invece di una modale
 * perché hanno form complessi con molte sezioni/tab.
 *
 * Formato endpoint: form/{entity}/{subtype}/{action}
 * Esempio: form/corsi/fad/new, form/corsi/aula/123
 *
 * action: "new" = creazione, altrimenti è l'id per edit/view
 */

export interface PageConfig {
  /** Route frontend (es. "/form/corsi/fad") */
  route: string;
  /** Nome componente per debug/documentazione */
  component: string;
  /** Tipo corso per passare nello state */
  courseType?: string;
}

/**
 * Registry: basePath → configurazione pagina
 *
 * Nota: solo le entità che richiedono pagine full-screen vanno qui.
 * Le entità con modali vanno in formRegistry.ts
 *
 * Le route seguono il pattern /form/{entity}/{type} per coerenza con gli endpoint backend.
 */
const pageMap: Record<string, PageConfig> = {
  "form/corsi/fad": {
    route: "/form/corsi/fad",
    component: "FadCorsoPage",
    courseType: "FAD",
  },
  "form/corsi/aula": {
    route: "/form/corsi/aula",
    component: "AulaCorsoPage",
    courseType: "Aula",
  },
  "form/corsi/misto": {
    route: "/form/corsi/misto",
    component: "MistoCorsoPage",
    courseType: "Misto",
  },
  "form/corsi/webinar": {
    route: "/form/corsi/webinar",
    component: "WebinarCorsoPage",
    courseType: "Webinar",
  },
};

interface PageEndpointParts {
  basePath: string;  // es. "form/corsi/fad"
  action: string;    // es. "new" o un ID
  isNew: boolean;
}

/**
 * Parsa un endpoint form per pagine
 * Supporta formati:
 * - form/corsi/fad/new -> basePath: form/corsi/fad, action: new
 * - form/corsi/fad/123 -> basePath: form/corsi/fad, action: 123
 * - form/corsi/fad/1/123 -> basePath: form/corsi/fad, action: 123 (1 è sub-tab, ignorata)
 */
export function parsePageEndpoint(endpoint: string): PageEndpointParts | null {
  const parts = endpoint.split("/");
  if (parts.length < 4 || parts[0] !== "form") {
    return null;
  }

  // Cerca il basePath più lungo che esiste nel pageMap
  // Prova da 3 parti (form/corsi/fad) fino a parts.length-1
  for (let i = 3; i < parts.length; i++) {
    const candidateBasePath = parts.slice(0, i).join("/");
    if (pageMap[candidateBasePath]) {
      // Trovato! L'action è l'ultimo elemento
      const action = parts[parts.length - 1];
      return {
        basePath: candidateBasePath,
        action,
        isNew: action === "new",
      };
    }
  }

  // Fallback: comportamento originale
  const action = parts[parts.length - 1];
  const basePath = parts.slice(0, -1).join("/");

  return {
    basePath,
    action,
    isNew: action === "new",
  };
}

/**
 * Verifica se un endpoint form è mappato a una pagina (invece che modale)
 */
export function isPageEndpoint(endpoint: string): boolean {
  const parsed = parsePageEndpoint(endpoint);
  if (!parsed) return false;
  return !!pageMap[parsed.basePath];
}

/**
 * Ottiene i dati di navigazione per un endpoint pagina
 * @param endpoint es. "form/corsi/fad/new" o "form/corsi/fad/123"
 * @param options opzioni di navigazione
 * @param options.data dati opzionali da passare (es. courseData per edit)
 * @param options.returnTo endpoint a cui tornare dopo OK/Annulla (es. "/get_page/corsi/fad/1")
 * @returns oggetto con route e state per navigate(), o null se non trovato
 */
export function getPageNavigation(
  endpoint: string,
  options?: { data?: Record<string, any>; returnTo?: string }
): { route: string; state: Record<string, any> } | null {
  const parsed = parsePageEndpoint(endpoint);
  if (!parsed) {
    console.warn(`Invalid page endpoint: ${endpoint}`);
    return null;
  }

  const config = pageMap[parsed.basePath];
  if (!config) {
    console.warn(`No page registered for: ${parsed.basePath}`);
    return null;
  }

  return {
    route: config.route,
    state: {
      mode: parsed.isNew ? "create" : "edit",
      courseType: config.courseType,
      id: parsed.isNew ? undefined : parsed.action,
      courseData: options?.data,
      returnTo: options?.returnTo,
    },
  };
}

/**
 * Ottiene la configurazione di una pagina dal basePath
 */
export function getPageConfig(basePath: string): PageConfig | null {
  return pageMap[basePath] || null;
}

/**
 * Lista tutti gli endpoint pagina registrati (utile per debug)
 */
export function getRegisteredPageEndpoints(): string[] {
  return Object.keys(pageMap);
}
